'use client'

import { useActionState, useId, useState } from 'react'
import Image from 'next/image'
import { ArrowRight, Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { Button, Checkbox, Field, Icon, Input, Pending } from '@/components/ui'
import { adresseInterne } from '@/lib/auth/roles'
import { connexion, type EtatFormulaire } from '../actions'
import { EnTetePorte, LienPorte } from '@/components/layout/Porte'

/**
 * Formulaire de connexion.
 *
 * L'indicateur d'aiguillage est **cosmétique**. Il reprend le geste de la
 * maquette — l'espace se devine à mesure qu'on tape — mais il ne décide rien :
 * la destination est choisie par le serveur à partir du rôle porté par le
 * compte.
 *
 * Il ne nomme d'ailleurs jamais l'espace interne précis. La maquette le déduit
 * du prénom pour les besoins de la démonstration ; le faire ici publierait
 * l'organigramme d'Umiros à quiconque tape une adresse au hasard.
 *
 * ------------------------------------------------------------------------
 * Passage en DA 2027 (2026-10-29) — ce qui a bougé, et ce qui n'a pas bougé
 * ------------------------------------------------------------------------
 * **Rien de la logique.** L'action serveur, `useActionState`, les `name=`,
 * l'aiguillage par domaine, les trois libellés de `decrireAiguillage`, la garde
 * anti-énumération, l'état `enCours` : identiques au caractère près.
 *
 * Ce qui a bougé est la présentation : champs `ton="oasis"`, indicateur en
 * étiquette + phrase, bouton jaune `action` (la seule action principale de
 * l'écran), et le bloc lavande « Première connexion ? » de la maquette validée.
 */
export function FormulaireConnexion({ suite }: { suite?: string }) {
  const [etat, action, enCours] = useActionState<EtatFormulaire, FormData>(connexion, {})
  const [email, setEmail] = useState('')
  const [voir, setVoir] = useState(false)
  const idEmail = useId()
  const idMotDePasse = useId()

  const indicateur = decrireAiguillage(email)

  return (
    <>
      <form action={action} className="flex flex-col gap-[26px]">
        <EnTetePorte surtitre="L’Oasis · Votre espace Umiros" titre="Se" accent="connecter.">
          Une seule adresse pour tout Umiros : votre espace s’ouvre selon votre profil.
        </EnTetePorte>

        {suite && <input type="hidden" name="suite" value={suite} />}

        <Field
          label="Adresse e-mail"
          htmlFor={idEmail}
          ton="oasis"
          error={etat.champ === 'email' ? etat.erreur : undefined}
        >
          <Input
            id={idEmail}
            name="email"
            type="email"
            autoComplete="email"
            required
            placeholder="vous@exemple.fr"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            invalid={etat.champ === 'email'}
            ton="oasis"
            icone={<Icon as={Mail} />}
          />
        </Field>

        {/* `aria-live="polite"` : le changement est annoncé sans interrompre la
          saisie en cours. */}
        <div
          aria-live="polite"
          className="-mt-2 ml-2.5 flex flex-wrap items-center gap-x-3 gap-y-2"
        >
          <span
            className={`inline-flex -rotate-2 items-center rounded-pill px-[11px] py-1 text-[13px] font-extrabold whitespace-nowrap text-ink ${indicateur.classe}`}
          >
            {indicateur.titre}
          </span>
          <p className="min-w-[16em] flex-1 text-[14px] text-ink-soft">{indicateur.detail}</p>
        </div>

        <Field
          label="Mot de passe"
          htmlFor={idMotDePasse}
          ton="oasis"
          error={etat.champ === 'motDePasse' ? etat.erreur : undefined}
        >
          {/* L'œil de la maquette : le mot de passe provisoire fait 20 caractères
            recopiés depuis un e-mail, la faute de frappe est le cas courant. */}
          <span className="relative block">
            <Input
              id={idMotDePasse}
              name="motDePasse"
              type={voir ? 'text' : 'password'}
              autoComplete="current-password"
              required
              invalid={etat.champ === 'motDePasse'}
              ton="oasis"
              icone={<Icon as={Lock} />}
              className="pr-14"
            />
            <button
              type="button"
              onClick={() => setVoir((v) => !v)}
              aria-label={voir ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              aria-pressed={voir}
              className="absolute top-1/2 right-2 grid size-11 -translate-y-1/2 place-items-center rounded-pill text-ink hover:bg-cream"
            >
              <Icon as={voir ? EyeOff : Eye} />
            </button>
          </span>
        </Field>

        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* La case est lue par `connexion()` : cochée, la session dure 30 jours ;
            décochée, le cookie meurt à la fermeture du navigateur. Elle était
            purement décorative jusqu'ici — affichée, cochée d'avance, et jamais
            consultée. Une commande qui ment sur ce qu'elle fait est pire qu'une
            commande absente, en particulier sur un poste partagé.

            ⚠️ Aucune marge en `className` sur cette case : en ton sombre elle
            porte `py-[13px] -my-[13px]` pour monter à 44px de cible tactile, et
            `Field.tsx` documente qu'un `mt-*` l'emporterait sur le `-my-*` en
            n'annulant qu'un côté. L'espacement vient du `gap` du parent. */}
          <Checkbox id="rester" name="rester" label="Rester connecté" defaultChecked ton="oasis" />

          {/* `min-h-[44px]` : la cible tactile du lien, alignée sur celle de la
            case à côté de lui. La rangée fait déjà 44px, elle ne bouge donc pas. */}
          <LienPorte href="/mot-de-passe-oublie">Mot de passe oublié ?</LienPorte>
        </div>

        {/* Erreur générale : celle qui ne vise aucun champ en particulier. Aplat
          rose, comme les erreurs de l'Oasis (`ZoneSignature`) — jamais de rouge
          dans la DA 2027. */}
        {etat.erreur && !etat.champ && (
          <p
            role="alert"
            className="rounded-[16px] bg-rose px-5 py-3.5 text-[15px] font-semibold text-ink"
          >
            {etat.erreur}
          </p>
        )}

        {/* Le jaune : la seule action principale de l'écran. */}
        <Button type="submit" variant="action" disabled={enCours} className="mt-1.5 self-start">
          {enCours ? (
            <>
              <Pending label="Connexion en cours" /> Connexion…
            </>
          ) : (
            <>
              Se connecter <Icon as={ArrowRight} size={18} />
            </>
          )}
        </Button>

        {/*
        La maquette montre ici un « Continuer avec Google » grisé, marqué
        « phase 2 ». Il n'est pas repris : Baptiste a tranché « pas de bouton
        inactif », et cet arbitrage est postérieur au handoff. Un bouton qui ne
        fait rien occupe la place, appelle le clic et n'apprend rien — annoncer
        une fonction absente n'est pas la rendre disponible. Il reviendra le jour
        où il marchera.
      */}
      </form>

      {/* Le cas des comptes ouverts par l'équipe : un mot de passe provisoire part
        par e-mail, et `/mot-de-passe` en fait choisir un à la première
        connexion. Illustration sur l'aplat lavande, jamais sur le crème. */}
      <div className="relative mt-[clamp(40px,4vw,72px)] rounded-[24px] bg-lavender py-[22px] pr-[120px] pl-[22px] oasis:py-6 oasis:pr-[clamp(150px,12vw,210px)] oasis:pl-[26px]">
        <p className="text-[18px] font-extrabold tracking-[-0.02em]">Première connexion ?</p>
        <p className="mt-1.5 max-w-[32em] text-[15px]">
          Utilisez le mot de passe provisoire reçu par e-mail. On vous demande d’en choisir un à
          vous juste après.
        </p>
        <Image
          src="/brand/stickers/sec-padlock.png"
          alt=""
          width={320}
          height={320}
          className="u-illustration absolute right-3.5 -bottom-3.5 h-auto w-[104px] rotate-6 oasis:right-[clamp(14px,1.4vw,28px)] oasis:w-[clamp(110px,8.5vw,160px)]"
        />
      </div>
    </>
  )
}

/**
 * Les trois états de l'indicateur, repris de la maquette.
 *
 * ⚠️ **Les libellés ne bougent pas d'un caractère** — en particulier celui de
 * l'accès interne, qui s'arrête volontairement avant de nommer le rôle. Seules
 * les classes changent : étiquette à trait en attente, rose pour l'équipe,
 * lavande pour un apprenant.
 *
 * ⚠️ Ce sont des chaînes **littérales** en table, jamais construites par
 * interpolation : Tailwind ne génère que ce qu'il lit tel quel dans le fichier.
 */
function decrireAiguillage(email: string) {
  if (!email.includes('@')) {
    return {
      titre: 'En attente de votre adresse',
      detail: 'L’espace est attribué automatiquement — vous n’avez rien à choisir.',
      classe:
        'bg-surface shadow-[inset_0_0_0_1.5px_var(--ink),0_0_0_3px_var(--creme),0_3px_6px_rgba(43,23,72,0.14)]',
    }
  }

  if (adresseInterne(email)) {
    return {
      titre: 'Accès équipe Umiros',
      // On s'arrête là : le rôle exact est porté par le compte et se découvre
      // après authentification, pas avant.
      detail: 'Adresse du domaine Umiros — votre espace interne s’ouvrira à la connexion.',
      classe: 'bg-rose shadow-[0_0_0_3px_var(--creme),0_3px_6px_rgba(43,23,72,0.18)]',
    }
  }

  return {
    titre: 'Accès apprenant',
    detail: 'Espace client — replays, supports, agenda et documents de votre parcours.',
    classe: 'bg-lavender shadow-[0_0_0_3px_var(--creme),0_3px_6px_rgba(43,23,72,0.18)]',
  }
}
